import { isUndefined, omitBy, sortBy } from 'lodash';

import { isNumeric } from '../utils/number';
import { findFlowCycles, newFlowId } from './flows';
import { newEmptyNode } from './nodes';
import { SankeyFlow, SankeyNode } from './types';

class JSInputParser {
  sourceLines: string[];
  uniqueNodes: Map<string, SankeyNode>;
  previousNodesArray: SankeyFormNode[];

  constructor(jsInput: string) {
    this.uniqueNodes = new Map();
    this.previousNodesArray = [];
    // Split input into lines, dropping any zero-width spaces which may have been
    // appended or prepended to lines (e.g. when pasted from PowerPoint), then trim again.
    this.sourceLines = jsInput.split('\n').map((line) =>
      line
        .replace(/^\u200B+/, '')
        .replace(/\u200B+$/, '')
        .trim()
    );
  }

  withPreviousNodes(previousNodes: SankeyFormNode[]) {
    this.previousNodesArray = previousNodes;
    return this;
  }

  parse(onError: (sourceLine: string) => void) {
    // The maximum number of decimal places in any input value.
    let maxDecimalPlaces = 0;
    const parsedFlows: SankeyFormFlow[] = [];

    const getPreviousNodeIdFromName = (name: string | undefined) =>
      this.previousNodesArray.find((n) => n.name === name)?.id;

    this.sourceLines.forEach((sourceLine) => {
      // If this is a blank line skip it entirely
      if (sourceLine === '') {
        return;
      }

      // Match node
      const nodeMatch = this.matchNode(sourceLine);
      if (nodeMatch) {
        this.patchNode({
          id: getPreviousNodeIdFromName(nodeMatch.name),
          ...nodeMatch,
        });
        return;
      }

      // Match flow
      const flowMatch = this.matchFlow(sourceLine);
      if (flowMatch) {
        const { source, amount, amountComparison, target } = flowMatch;
        const dataGridRowIndex = parsedFlows.length;

        // Save the flow regardless of errors
        parsedFlows.push({
          id: newFlowId(dataGridRowIndex),
          source,
          target,
          amount: isNumeric(amount) ? Number(amount) : NaN,
          amountComparison: isNumeric(amountComparison)
            ? Number(amountComparison)
            : NaN,
          dataGridRowIndex,
        });

        // Assign appearance indexes to all the nodes by the order of appearance in the data grid.
        // Flow source nodes get precedence over target nodes, so we add 0.5 to the target's appearance index.
        this.patchNode({ name: source, appearanceIndex: dataGridRowIndex });
        this.patchNode({
          name: target,
          appearanceIndex: dataGridRowIndex + 0.5,
        });

        // Update the maximum number of decimal places with the current amount's one
        const amountDecimalPlaces = (amount.split(/\./)[1] || '').length;
        maxDecimalPlaces = Math.max(maxDecimalPlaces, amountDecimalPlaces);
        return;
      }

      // If this line can't be parsed into neither a flow nor a node, we reporting it as a diagram parsing error.
      // error to Sentry and continue parsing the rest of the diagram without notifying the user.
      onError(sourceLine);
    });

    // Construct the final list of approved_nodes, sorted by their names
    const nodes = sortBy(Array.from(this.uniqueNodes.values()), (n) => n.name);

    // Check for flow cycles
    const cycles = findFlowCycles(parsedFlows);

    // Make the final list of flows with node objects instead of names
    const flows = parsedFlows.map((flow): SankeyFlow => {
      const source = nodes.find((node) => node.name === flow.source);
      const target = nodes.find((node) => node.name === flow.target);
      if (!source || !target) {
        throw new Error(
          `Source or target node not saved in map: ${JSON.stringify(flow)}`
        );
      }

      // Back flows will be faded in the diagram and rendered after the nodes
      // have already been positioned. We let the flow detection algorithm decide
      // which flow of each cycle will be marked as back flow.
      const isBackFlow = cycles.some(
        ({ backFlow: { source, target } }) =>
          source === flow.source && target === flow.target
      );

      return {
        source,
        target,
        isBackFlow,
        amount: flow.amount,
        amountComparison: flow.amountComparison,
        dataGridRowIndex: flow.dataGridRowIndex,
        id: flow.id,
        dx: NaN,
        dy: NaN,
        sy: NaN,
        ty: NaN,
      };
    });

    return { nodes, flows, cycles, maxDecimalPlaces };
  }

  patchNode(patch: Partial<SankeyNode> & { name: string }) {
    const existingNode = this.uniqueNodes.get(patch.name);

    // If the node's appearanceIndex is already set, don't overwrite it (we want to keep the minimum one)
    if (existingNode?.appearanceIndex !== undefined) {
      patch.appearanceIndex = undefined;
    }

    this.uniqueNodes.set(patch.name, {
      ...newEmptyNode(patch.name),
      ...existingNode,
      ...omitBy(patch, isUndefined),
    });
  }

  matchNode(sourceLine: string) {
    // Standard format: ":name color [use_custom_amount_label] custom_amount_label [use_custom_comparison_label] custom_comparison_label"
    let match = sourceLine.match(
      /^:(.*)\s*(#[0-9A-F]{3,6})\s*\[(\w*)\]\s*(.*)\s*\[(\w*)\](.*)\s*$/i
    );
    if (match) {
      return {
        name: match[1].trim(), // The first (.*) bit
        color: match[2], // The (#[0-9A-F]{3,6}) bit
        use_custom_amount_label: match[3] === 'true', // The first (\w*) bit
        custom_amount_label: match[4], // The second (.*) bit
        use_custom_comparison_label: match[5] === 'true', // The second (\w*) bit
        custom_comparison_label: match[6], // The third (.*) bit
      };
    }

    // Legacy format: ":name color"
    match = sourceLine.match(/^:(.*)\s*(#[0-9A-F]{3,6})$/i);
    if (match) {
      return {
        name: match[1].trim(), // The (.*) bit
        color: match[2], // The (#[0-9A-F]{3,6}) bit
        use_custom_amount_label: false,
        custom_amount_label: '',
        use_custom_comparison_label: false,
        custom_comparison_label: '',
      };
    }

    return undefined;
  }

  matchFlow(sourceLine: string) {
    // Standard format: "source [amount][amountComparison] target"
    let match = sourceLine.match(/^(.*)\s*\[(.*)\]\[(.*)\]\s*(.*)$/);
    if (match) {
      return {
        source: match[1].trim(), // The first (.*) bit
        amount: match[2], // The second (.*) bit
        amountComparison: match[3], // The third (.*) bit
        target: match[4].trim(), // The fourth (.*) bit
      };
    }

    // Legacy format: "source [amount] target"
    match = sourceLine.match(/^(.*)\s*\[(.*)\]\s*(.*)$/);
    if (match) {
      return {
        source: match[1].trim(), // The first (.*) bit
        amount: match[2], // The second (.*) bit
        amountComparison: '',
        target: match[3].trim(), // The third (.*) bit
      };
    }

    return undefined;
  }
}

export default JSInputParser;
